import apiServiceGenerator from "@/api/generator";
import {changes, loading, alert, handleError} from './helpers'

/**
 * A fordítás-csomagot a felület induláskor (és nyelvváltáskor) kéri le, és a böngészőben
 * tartja. Egy most mentett fordítás ezért teljes újratöltésig nem látszana: a törzsadat
 * neve a régi maradna azon a képernyőn, ahol épp átírták (#182). Ha a mentés fordítást is
 * vitt, újrakérjük - máskor nem, mert ez a teljes csomag.
 */
function refreshTranslations(dispatch, payload) {
    if (payload?.translations && Object.keys(payload.translations).length) {
        dispatch('trans/translations', null, {root: true});
    }
}

export default function (endpoint, /*namespace*/) {

    let pending = {};

    function normalizeFilters(filters) {
        return JSON.stringify(Object.keys(filters).sort().reduce((acc, key) => {
            acc[key] = filters[key];
            return acc;
        }, {}))
    }

    return {
        namespaced: true,

        state: {
            loading: false,
            pager: {},
            additional: {},
            list: [],
            listError: null,
            all: [],
            allQuery: {},
            item: {},
            // a szerkesztett sor betöltése: {id, status: 'loading' | 'loaded' | 'failed'}
            itemLoad: {},
            errors: {},
            fields: [],
            service: apiServiceGenerator(endpoint),
            cache: {}
        },

        getters: {
            loading: function (state) {
                return !!state.loading
            },
            itemLoad: function (state) {
                return state.itemLoad
            },
            pager: function (state) {
                return JSON.parse(JSON.stringify(state.pager))
            },
            all: function (state) {
                return JSON.parse(JSON.stringify(state.all))
            },
            list: function (state) {
                return state.list
            },
            listError: function (state) {
                return state.listError
            },
            item: function (state) {
                return JSON.parse(JSON.stringify(state.item))
            },
            additional: function (state) {
                return JSON.parse(JSON.stringify(state.additional))
            },
            states: function (state) {
                const states = {};

                Object.keys(state.errors).forEach(key => {
                        states[key] = false
                    }
                )

                return states
            },
            ordered: (state) => (prop) => {
                return JSON.parse(JSON.stringify(state.list)).sort(function (a,b) {
                    return a[prop] < b[prop]
                        ? -1
                        : (
                            a[prop] > b[prop]
                            ? 1
                            : 0
                        )
                })
            },
            errors: function (state) {
                const errors = Object.assign({}, (state.errors || {}))

                Object.keys(errors).forEach(key => {
                    errors[key] = errors[key].join(' ')
                })

                return errors;
            },
            fields: function (state) {
                return state.fields
            },
        },

        mutations: {
            'SET_LOADING': function (state, boolean = true) {
                state.loading = boolean
            },

            'SET_ALL_QUERY': function (state, query = {}) {
                state.allQuery = query
            },

            'SET_ALL': function (state, all) {
                state.all = all
            },

            'SET_LIST': function (state, list) {
                state.list = list
            },

            'SET_LIST_ERROR': function (state, error = null) {
                state.listError = error
            },

            'SET_ITEM': function (state, item = {}) {
                state.item = item
            },

            'SET_ITEM_LOAD': function (state, itemLoad = {}) {
                state.itemLoad = itemLoad
            },

            'UPDATE_ITEM': function (state, props) {
                Object.keys(props).forEach(prop => {
                    state.item[prop] = props[prop]
                })
            },

            'ADD_ITEM': function (state, item) {
                if (!state.list) {
                    state.list = [];
                }

                state.list.unshift(item)
            },

            'PUSH_ITEM': function (state, item) {
                if (!state.list) {
                    state.list = []
                }

                state.list.push(item)
                state.all.push(item)
            },

            'PUSH_ITEM_INTO_ALL': function (state, item) {
                if (!state.all) {
                    state.all = []
                }

                state.all.push(item);
            },

            'REPLACE_ITEM': function (state, item) {
                const index = state.list.findIndex(listItem => listItem.id === item.id);

                if (index !== -1) {
                    state.list[index] = item
                    state.list = JSON.parse(JSON.stringify(state.list))
                }

                const allIndex = state.all.findIndex(allItem => allItem.id === item.id);

                if (allIndex !== -1) {
                    state.all[allIndex] = item
                    state.all = JSON.parse(JSON.stringify(state.all))
                }
            },

            'REMOVE_ITEM': function (state, id) {
                const index = state.list.findIndex(item => item.id === id);

                if (index !== -1) {
                    state.list.splice(index, 1)
                }

                const allIndex = state.all.findIndex(item => item.id === id);

                if (allIndex !== -1) {
                    state.all.splice(index, 1)
                }
            },

            'SET_PAGER': function (state, pager = {}) {
                state.pager = pager
            },

            'SET_ADDITIONAL': function (state, data = {}) {
                state.additional = data
            },

            'SET_ERRORS'(state, errors) {
                state.errors = errors
            },

            'SET_ERROR'(state, error) {
                const errors = Object.assign({}, state.errors)

                Object.keys(error).forEach(key => {
                    errors[key]
                        ? errors[key].push(error[key])
                        : errors[key] = Array(error[key])
                })

                state.errors = errors
            },

            'SET_FIELDS': function(state, fields) {
                state.fields = fields
            },

            'CLEAR_CACHE': function (state) {
                state.cache = {};
            },
            'ADD_TO_CACHE': function (state, {key, result}) {
                state.cache[key] = {
                    result: result.data,
                    additional: result.additional,
                    meta: result.data
                };
            },
        },

        actions: {
            all: function ({commit, state, getters}, force) {
                if (!state.all || !state.all.length || force) {

                    let data = typeof force === 'object' ? force : undefined;

                    const filterKey = data ? normalizeFilters(data) : 'empty';

                    // `true` = friss lista (pl. létrehozás, másolás után): a régi szűrésű találatok sem érvényesek
                    if (force === true) {
                        commit('CLEAR_CACHE');
                        delete pending[filterKey];
                    }

                    if (state.cache[filterKey]) {
                        // a megjelenített lista is ez legyen: egy másik szűrésű lekérdezés felülírhatta
                        commit('SET_ALL', state.cache[filterKey].result);
                        return state.cache[filterKey];
                    }

                    loading(commit);

                    if (pending[filterKey]) {
                        return pending[filterKey];
                    }

                    if (data) {
                        commit('SET_ALL_QUERY', data);
                    }
                    const request = state.service.fetchAll(data).then(response => {
                        commit('ADD_TO_CACHE', {key: filterKey, result: response});
                        commit('SET_ALL', response.data)
                        loading(commit, false)
                        return response.data
                    }).catch((e) => {
                        delete pending[filterKey];
                        handleError(e, commit);
                    });

                    pending[filterKey] = request;

                    return request;
                } else {
                    return getters.all;
                }
            },

            list: function ({commit, state}, filters) {
                loading(commit)

                return state.service.fetchList(filters).then(response => {
                    commit('SET_LIST_ERROR', null)
                    commit('SET_LIST', response.data)
                    commit('SET_PAGER', response.meta)
                    commit('SET_ADDITIONAL', response.additional)
                    loading(commit, false)
                    return response.data
                }).catch((e) => {
                    // A korábbi találatok nem maradhatnak a képernyőn: a szűrés vagy a keresés
                    // eredményének látszanának, változatlan darabszámmal, holott a lekérdezés
                    // le sem futott.
                    commit('SET_LIST', [])
                    commit('SET_PAGER', {})
                    commit('SET_ADDITIONAL', {})
                    commit('SET_LIST_ERROR', {status: e?.response?.status || null})

                    handleError(e, commit)
                })
            },

            show: function ({commit, state}, {id, query}) {
                loading(commit)

                // Másik sor érkezik: az azonosító azonnal a helyén van, így a lap a betöltés
                // alatt is szerkesztést mutat, és a mentés a meglévő sort módosítja, nem újat hoz létre.
                if (String(state.item?.id) !== String(id)) {
                    commit('SET_ITEM', {id})
                }
                commit('SET_ITEM_LOAD', {id, status: 'loading'})

                return state.service.fetchItem(id, query).then(data => {
                    commit('SET_ITEM', data.data)
                    commit('SET_ITEM_LOAD', {id, status: 'loaded'})
                    loading(commit, false)
                    return data
                }).catch((e) => {
                    commit('SET_ITEM_LOAD', {id, status: 'failed', code: e?.response?.status ?? null})
                    handleError(e, commit)
                })
            },

            select: function ({commit, state}, id) {
                loading(commit)

                let item = state.list.find(item => item.id === id);

                if (!item) {
                    state.all.find(item => item.id === id)
                }

                if (item) {
                    commit('SET_ITEM', item);
                    loading(commit, false)
                    return item;
                } else {
                    loading(commit, false)
                }
            },

            create: function ({commit, state, dispatch}, payload) {
                loading(commit)

                return state.service.store(payload).then(data => {
                    const model = Object.assign({}, payload, data.data);

                    commit('ADD_ITEM', model);
                    commit('SET_ITEM', model);
                    alert(commit, this.$t('request.creation_is_successful'));
                    commit('SET_ERRORS', {});
                    refreshTranslations(dispatch, payload);
                    // dispatch('all', state.allQuery);

                    loading(commit, false)

                    return model;
                }).catch((e) => {
                    handleError(e, commit)
                })
            },

            addToAll({commit}, item) {
                commit('PUSH_ITEM_INTO_ALL', item);
            },

            createConfig: function ({commit, state, dispatch}, { push, payload}) {
                loading(commit)

                return state.service.store(payload).then(data => {
                    const model = Object.assign({}, payload, data.data)

                    commit(push ? 'PUSH_ITEM' : 'ADD_ITEM', model)
                    commit('SET_ITEM', model)
                    alert(commit, this.$t('request.creation_is_successful'))
                    commit('SET_ERRORS', {});
                    dispatch('all', true);

                    loading(commit, false)

                    return model
                }).catch((e) => {
                    handleError(e, commit)
                })
            },

            update: function ({commit, state, dispatch}, payload) {
                loading(commit)

                const { id, data } = changes(payload, state.item)

                const content = Object.assign({_method: 'PUT'}, data);
                return state.service.update(id, content).then(response => {
                    const model = Object.assign({}, data, response.data)

                    commit('REPLACE_ITEM', model);
                    commit('SET_ITEM', model);
                    alert(commit, this.$t('request.save_is_successful'))
                    commit('SET_ERRORS', {});
                    refreshTranslations(dispatch, data);
                    loading(commit, false)

                    return model;
                }).catch((e) => {
                    handleError(e, commit)
                })
            },

            destroy: function ({commit, state}, id) {
                loading(commit)

                return state.service.destroy(id).then(response => {
                    commit('REMOVE_ITEM', id);
                    commit('SET_ITEM', {});
                    alert(commit, this.$t('request.deletion_is_successful'))
                    loading(commit, false)

                    return response;
                }).catch((e) => {
                    handleError(e, commit)
                    // a hívó (pl. a lista) ne mutasson sikert egy elutasított törlésre
                    throw e
                });
            },

            push: function ({commit}, item) {
                commit('PUSH_ITEM', item)
            },

            setItem: function ({commit}, payload) {
                commit('SET_ITEM', payload)
            },

            setAll: function ({commit}, payload) {
                commit('SET_ALL', payload)
            },

            clearItem: function ({commit}) {
                commit('SET_ITEM_LOAD')
                commit('SET_ITEM')
            },

            clearList: function ({commit}) {
                commit('SET_LIST', [])
                commit('SET_PAGER', {})
                commit('SET_ADDITIONAL', {})
                commit('SET_LIST_ERROR', null)
            },

            clearAll: function ({commit}) {
                commit('SET_ALL', [])
            },

            clearErrors: function ({commit}) {
                commit('SET_ERRORS', {})
            },

            clearModule: function ({dispatch}) {
                dispatch('clearItem')
                dispatch('clearList')
                // dispatch('clearAll')
                dispatch('clearErrors')
            },

            replaceItem: function ({commit}, item) {
                commit('REPLACE_ITEM', item);
            },

            fields: function ({commit, rootState}, payload) {
                loading(commit)

                return rootState.field.service.fetchList(payload).then(response => {
                    commit('SET_FIELDS', response.data)
                    loading(commit, false)
                    return response.data
                }).catch((e) => {
                    handleError(e, commit)
                })
            },
        },
    }
}
